import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Logo } from "../components/Layout";
import { LogOut, Plus, Trash2, Edit3, Save, X } from "lucide-react";

const useAuth = () => {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient.get("/auth/me").then(r => setMe(r.data)).catch(() => setMe(false)).finally(() => setLoading(false));
  }, []);
  return { me, loading, setMe };
};

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await apiClient.post("/auth/login", { email, password });
      if (r.data.token) localStorage.setItem("eema_token", r.data.token);
      navigate("/admin");
    } catch (err) {
      toast.error("Invalid credentials");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] px-6" data-testid="page-admin-login">
      <Toaster position="top-right" richColors/>
      <div className="w-full max-w-md bg-white p-10 rounded-sm border border-slate-200 shadow-xl">
        <Logo/>
        <h1 className="mt-8 font-display font-bold text-2xl">Admin Sign in</h1>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568] font-display block mb-2">Email</span>
            <input data-testid="admin-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required className="w-full border border-slate-200 px-4 py-3 rounded-sm focus:border-[#003399] focus:outline-none"/>
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568] font-display block mb-2">Password</span>
            <input data-testid="admin-password" type="password" value={password} onChange={e=>setPassword(e.target.value)} required className="w-full border border-slate-200 px-4 py-3 rounded-sm focus:border-[#003399] focus:outline-none"/>
          </label>
          <button type="submit" data-testid="admin-login-submit" disabled={loading} className="btn-primary w-full mt-4">{loading ? "…" : "Sign in"}</button>
        </form>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const { me, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // article being edited or "new"

  useEffect(() => {
    if (loading) return;
    if (!me) { navigate("/admin/login"); return; }
    loadAll();
  }, [me, loading]);

  const loadAll = async () => {
    try {
      const [a, i, m] = await Promise.all([
        apiClient.get("/admin/articles"),
        apiClient.get("/admin/partner-inquiries"),
        apiClient.get("/admin/contact-messages"),
      ]);
      setArticles(a.data); setInquiries(i.data); setMessages(m.data);
    } catch {}
  };

  const logout = async () => {
    await apiClient.post("/auth/logout").catch(()=>{});
    localStorage.removeItem("eema_token");
    navigate("/admin/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">…</div>;
  if (!me) return null;

  const saveArticle = async (payload) => {
    try {
      if (editing === "new") {
        await apiClient.post("/admin/articles", payload);
        toast.success("Article created");
      } else {
        await apiClient.put(`/admin/articles/${editing.id}`, payload);
        toast.success("Article updated");
      }
      setEditing(null);
      loadAll();
    } catch { toast.error("Save failed"); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await apiClient.delete(`/admin/articles/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB]" data-testid="page-admin">
      <Toaster position="top-right" richColors/>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo/>
          <div className="flex items-center gap-6">
            <span className="text-sm text-[#4A5568]">{me.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="text-sm text-[#003399] hover:text-[#00E5FF] inline-flex items-center gap-1.5"><LogOut size={14}/> Logout</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex gap-6 border-b border-slate-200 mb-8">
          {[["articles","Articles"],["inquiries","Partner Inquiries"],["messages","Contact Messages"]].map(([k,l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={()=>setTab(k)} className={`py-3 px-1 text-sm font-display font-medium border-b-2 -mb-px ${tab===k?'border-[#003399] text-[#003399]':'border-transparent text-[#4A5568] hover:text-[#003399]'}`}>{l}</button>
          ))}
        </div>

        {tab === "articles" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-semibold text-2xl">Articles ({articles.length})</h2>
              <button onClick={()=>setEditing("new")} data-testid="admin-new-article" className="btn-primary inline-flex"><Plus size={16}/> New</button>
            </div>
            {editing ? (
              <ArticleEditor article={editing==="new"?null:editing} onSave={saveArticle} onCancel={()=>setEditing(null)}/>
            ) : (
              <div className="bg-white rounded-sm border border-slate-200 divide-y divide-slate-200">
                {articles.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-4">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-[#0B1120] truncate">{a.title}</div>
                      <div className="text-xs text-slate-400 mt-1">{a.category} · {a.language} · {a.published?'Published':'Draft'} · {new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`edit-${a.id}`} onClick={()=>setEditing(a)} className="p-2 hover:bg-slate-50 text-[#003399]"><Edit3 size={16}/></button>
                      <button data-testid={`delete-${a.id}`} onClick={()=>deleteArticle(a.id)} className="p-2 hover:bg-slate-50 text-red-500"><Trash2 size={16}/></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "inquiries" && (
          <div>
            <h2 className="font-display font-semibold text-2xl mb-6">Partner Inquiries ({inquiries.length})</h2>
            <div className="space-y-4">
              {inquiries.map(i => (
                <div key={i.id} className="bg-white p-6 rounded-sm border border-slate-200">
                  <div className="flex justify-between mb-3">
                    <div className="font-display font-semibold text-[#0B1120]">{i.company_name} · {i.country}</div>
                    <div className="text-xs text-slate-400">{new Date(i.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-[#4A5568]">{i.contact_name} ({i.role}) — <a href={`mailto:${i.email}`} className="text-[#003399]">{i.email}</a></div>
                  <div className="text-xs text-slate-500 mt-1">Partnership: {i.partnership_type} · Website: {i.website || '-'}</div>
                  <div className="mt-3 text-sm whitespace-pre-line text-[#0B1120]">{i.message}</div>
                </div>
              ))}
              {inquiries.length===0 && <div className="text-center text-slate-400 py-12">No inquiries yet.</div>}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div>
            <h2 className="font-display font-semibold text-2xl mb-6">Contact Messages ({messages.length})</h2>
            <div className="space-y-4">
              {messages.map(m => (
                <div key={m.id} className="bg-white p-6 rounded-sm border border-slate-200">
                  <div className="flex justify-between mb-3">
                    <div className="font-display font-semibold text-[#0B1120]">{m.subject}</div>
                    <div className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-[#4A5568]">{m.name} ({m.company || '-'}) — <a href={`mailto:${m.email}`} className="text-[#003399]">{m.email}</a></div>
                  <div className="mt-3 text-sm whitespace-pre-line text-[#0B1120]">{m.message}</div>
                </div>
              ))}
              {messages.length===0 && <div className="text-center text-slate-400 py-12">No messages yet.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ArticleEditor({ article, onSave, onCancel }) {
  const [f, setF] = useState({
    title: article?.title || "",
    excerpt: article?.excerpt || "",
    content: article?.content || "",
    category: article?.category || "News",
    cover_image: article?.cover_image || "",
    language: article?.language || "en",
    published: article?.published ?? true,
    slug: article?.slug || "",
  });
  const set = (k) => (e) => setF(x => ({...x, [k]: e.target.type==="checkbox"?e.target.checked:e.target.value}));
  return (
    <div className="bg-white p-8 rounded-sm border border-slate-200">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="md:col-span-2"><span className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Title</span><input data-testid="edit-title" value={f.title} onChange={set("title")} className="w-full border border-slate-200 px-4 py-3 rounded-sm focus:border-[#003399] focus:outline-none"/></label>
        <label><span className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Category</span><input value={f.category} onChange={set("category")} className="w-full border border-slate-200 px-4 py-3 rounded-sm"/></label>
        <label><span className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Language</span>
          <select value={f.language} onChange={set("language")} className="w-full border border-slate-200 px-4 py-3 rounded-sm"><option value="en">EN</option><option value="it">IT</option><option value="de">DE</option><option value="fr">FR</option></select>
        </label>
        <label className="md:col-span-2"><span className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Cover image URL</span><input value={f.cover_image} onChange={set("cover_image")} className="w-full border border-slate-200 px-4 py-3 rounded-sm"/></label>
        <label className="md:col-span-2"><span className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Excerpt</span><textarea data-testid="edit-excerpt" value={f.excerpt} onChange={set("excerpt")} rows={2} className="w-full border border-slate-200 px-4 py-3 rounded-sm resize-none"/></label>
        <label className="md:col-span-2"><span className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Content</span><textarea data-testid="edit-content" value={f.content} onChange={set("content")} rows={10} className="w-full border border-slate-200 px-4 py-3 rounded-sm resize-none"/></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.published} onChange={set("published")}/> <span className="text-sm">Published</span></label>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onCancel} data-testid="edit-cancel" className="btn-outline"><X size={16}/> Cancel</button>
        <button onClick={()=>onSave(f)} data-testid="edit-save" className="btn-primary"><Save size={16}/> Save</button>
      </div>
    </div>
  );
}
